import { RequestedServices } from './RequestedServices';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Link,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { isContractorRole } from '@moko/utils/src/authHelper';
import {
  getTenantIdFromToken,
  isTenantFlagOn,
} from '@moko/utils/src/tenantHelper';
import { Features, useFeatureFlag } from '@moko/utils/src/featureAccess';
import type { WorkComponentProps, IWorkDetailsServices } from './types';
import EditItem, { type IEditItemToolbarButton } from '../EditItem';
import { useTenantWorkTypes } from './useTenantWorkTypes';
import { FIELD_LABEL_COLUMNS } from '../OtherInfo/panelLayout';
import { useTenant } from '../TenantProvider';
import TitledCard from '../Layout/TitledCard';
import { Briefcase20Regular } from '@fluentui/react-icons';
import OrderDetailsDrawer, {
  type GetOrderService,
  type GetOrderMetadataService,
} from './OrderDetailsDrawer';

interface WorkDetailsProps extends WorkComponentProps {
  workId: number;
  services: IWorkDetailsServices;
  loadedWork?: Record<string, unknown> | null;
  getOrder?: GetOrderService;
  getOrderMetadata?: GetOrderMetadataService;
  postButtons?: IEditItemToolbarButton[];
  onWorkSaved?: (work: {
    description?: string | null;
    statusId?: number | null;
    specialInstruction?: string | null;
  }) => void;
  readOnly?: boolean;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

const useStyles = makeStyles({
  headerGrid: {
    display: 'grid',
    gridTemplateColumns: FIELD_LABEL_COLUMNS,
    columnGap: '8px',
    rowGap: '12px',
    alignItems: 'center',
  },
  headerLabel: {
    overflowWrap: 'break-word',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  headerValue: {
    overflowWrap: 'break-word',
    minWidth: 0,
  },
  workTypeHint: {
    gridColumn: '2',
    marginTop: '-8px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  instruction: {
    overflowWrap: 'break-word',
    color: tokens.colorPaletteGreenForeground1,
  },
});

const textOrNull = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
};

const WorkDetails = ({
  workId,
  backPath,
  services,
  loadedWork,
  getOrder,
  getOrderMetadata,
  postButtons,
  onWorkSaved,
  setSuccessMessage,
  setErrorMessage,
  readOnly = false,
  editing,
  onEditingChange,
}: WorkDetailsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const styles = useStyles();
  const { tenant } = useTenant();
  const showAssetInCreateWork = isTenantFlagOn(
    tenant?.otherInfo,
    'showAssetInCreateWork'
  );
  const standardInstruction = tenant?.otherInfo?.standardInstruction?.trim();
  const { isEnabled: isAssetsEnabled } = useFeatureFlag(
    Features.FEATURE_ASSETS
  );
  const { isEnabled: isZonesEnabled } = useFeatureFlag(Features.FEATURE_ZONES);
  const isSaved = useRef(false);
  const [orderId, setOrderId] = useState<number | undefined>();
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [createWorkTypeId, setCreateWorkTypeId] = useState<number | null>(null);

  const isExistingWork = !!workId && !Number.isNaN(workId);
  const workTenantId =
    typeof loadedWork?.tenantId === 'number'
      ? loadedWork.tenantId
      : getTenantIdFromToken() ?? undefined;
  const [seenWorkTenantId, setSeenWorkTenantId] = useState(workTenantId);
  if (seenWorkTenantId !== workTenantId) {
    setSeenWorkTenantId(workTenantId);
    if (seenWorkTenantId != null && workTenantId != null) {
      setCreateWorkTypeId(null);
    }
  }
  const workTypes = useTenantWorkTypes(
    workTenantId,
    typeof services.getTenantWorkTypes === 'function',
    services.getTenantWorkTypes
  );
  const savedWorkTypeId =
    typeof loadedWork?.workTypeId === 'number' ? loadedWork.workTypeId : null;
  const pageEdit = onEditingChange != null;

  const contractorView = isContractorRole();
  const orderLink =
    getOrder && orderId !== undefined ? (
      <>
        <span className={styles.headerLabel}>
          {t('workOrderId', 'Work Order ID')}:
        </span>
        {contractorView ? (
          <span className={styles.headerValue}>
            {t('order', 'Order')} #{orderId}
          </span>
        ) : (
          <Link onClick={() => setOrderDrawerOpen(true)}>
            {t('order', 'Order')} #{orderId}
          </Link>
        )}
      </>
    ) : null;

  const instructionRow = standardInstruction ? (
    <>
      <span className={styles.headerLabel}>
        {t('instruction', 'Instruction')}:
      </span>
      <span className={styles.instruction}>{standardInstruction}</span>
    </>
  ) : null;
  const specialInstruction = textOrNull(loadedWork?.specialInstruction);
  const specialInstructionRow =
    contractorView && specialInstruction ? (
      <>
        <span className={styles.headerLabel}>
          {t('specialInstruction', 'Special instruction')}:
        </span>
        <span className={styles.headerValue}>{specialInstruction}</span>
      </>
    ) : null;
  const showSpecialInstructionField =
    !contractorView &&
    (!isExistingWork || editing || Boolean(specialInstruction));

  const workTypeId = isExistingWork ? savedWorkTypeId : createWorkTypeId;
  const workTypeName = workTypes.options.find(
    (option) => option.id === workTypeId
  )?.name;
  const workTypeLabel =
    workTypeId == null
      ? t('noWorkType', 'No work type')
      : workTypeName ??
        t('unavailableWorkType', 'Unavailable work type ({{id}})', {
          id: workTypeId,
        });
  const workTypeLoading =
    !!services.getTenantWorkTypes &&
    (workTypes.loading || (isExistingWork && loadedWork == null));
  const workTypeRow = services.getTenantWorkTypes ? (
    <>
      <span className={styles.headerLabel}>{t('workType', 'Work type')}:</span>
      {workTypeLoading ? (
        <Spinner size="tiny" />
      ) : workTypes.error ? (
        <span className={styles.headerValue}>
          {t('workTypesLoadFailed', 'Could not load work types.')}{' '}
          <Button
            appearance="transparent"
            size="small"
            onClick={workTypes.refetch}
          >
            {t('retry', 'Retry')}
          </Button>
        </span>
      ) : isExistingWork ? (
        <span className={styles.headerValue}>{workTypeLabel}</span>
      ) : (
        <>
          <Dropdown
            style={{ minWidth: 'auto', width: '100%' }}
            value={workTypeName ?? t('noWorkType', 'No work type')}
            selectedOptions={[workTypeId == null ? '' : String(workTypeId)]}
            disabled={readOnly}
            onOptionSelect={(_, data) => {
              const next = data.optionValue;
              setCreateWorkTypeId(
                next == null || next === '' ? null : Number(next)
              );
            }}
          >
            <Option value="">{t('noWorkType', 'No work type')}</Option>
            {workTypes.options.map((option) => (
              <Option key={option.id} value={String(option.id)}>
                {option.name}
              </Option>
            ))}
          </Dropdown>
          {workTypes.options.length === 0 && (
            <span className={styles.workTypeHint}>
              {t('noWorkTypes', 'No work types for this tenant.')}
            </span>
          )}
        </>
      )}
    </>
  ) : null;

  const headerSlot =
    workTypeRow ||
    orderLink ||
    instructionRow ||
    specialInstructionRow ||
    loadedWork?.otherInfo ? (
      <div className={styles.headerGrid}>
        {workTypeRow}
        {orderLink}
        {instructionRow}
        {specialInstructionRow}
        <div style={{ gridColumn: '1 / -1' }}>
          <RequestedServices otherInfo={loadedWork?.otherInfo} />
        </div>
      </div>
    ) : undefined;

  return (
    <TitledCard
      title={t('workDetails', 'Work details')}
      icon={<Briefcase20Regular />}
    >
      <EditItem
        actionsPlacement="bottom"
        hideToolbar={readOnly}
        otherInfoReadOnly={pageEdit ? !editing : readOnly}
        otherInfoCanToggleEdit={pageEdit}
        editing={editing}
        onEditingChange={onEditingChange}
        headerSlot={headerSlot}
        isDirtyExternal={!isExistingWork && createWorkTypeId != null}
        getAction={async (abortController, id) => {
          const work: any = loadedWork
            ? { ...loadedWork }
            : await services.getWork(abortController, id);
          const rawOrderId = work?.orderId;
          setOrderId(
            typeof rawOrderId === 'number' && rawOrderId > 0
              ? rawOrderId
              : undefined
          );
          work.assigneeName = (work.assigneeName ?? '').toString();
          return work;
        }}
        newAction={async (abortController, newWork) => {
          try {
            const res = await services.createWork(abortController, {
              description: newWork.description as string | undefined,
              specialInstruction: textOrNull(newWork.specialInstruction),
              ...(services.getTenantWorkTypes
                ? { workTypeId: createWorkTypeId }
                : {}),
            });
            onWorkSaved?.({
              description: newWork.description as string | undefined,
            });
            setSuccessMessage(
              `Work with id: ${res.workId} successfully created.`
            );
            navigate(`${backPath}/edit?id=${res.workId}`);
          } catch (error) {
            setErrorMessage(
              (error as { message?: string })?.message ??
                'Failed to create work.'
            );
            throw error;
          }
        }}
        editAction={async (abortController, id, editedWork) => {
          try {
            const description = editedWork.description as string | undefined;
            await services.updateWork(abortController, id, {
              description,
              ...(contractorView
                ? {}
                : {
                    specialInstruction: textOrNull(
                      editedWork.specialInstruction
                    ),
                  }),
            });
            onWorkSaved?.({
              description,
              ...(contractorView
                ? {}
                : {
                    specialInstruction: textOrNull(
                      editedWork.specialInstruction
                    ),
                  }),
            });
            setSuccessMessage(`Work with id: ${id} successfully updated.`);
          } catch (error: any) {
            console.error('Error:', error);
            setErrorMessage(error?.message ?? `Failed to update work ${id}.`);
          }
        }}
        isSaved={isSaved}
        back={backPath}
        postButtons={postButtons}
        metadata={{
          fields: [
            ...(isExistingWork
              ? [
                  {
                    name: 'assigneeName',
                    fieldType: 'Text',
                    label: 'Assignee',
                    placeholder: 'No assigned contractor',
                  },
                ]
              : []),
            ...(showSpecialInstructionField
              ? [
                  {
                    name: 'specialInstruction',
                    fieldType: 'String',
                    label: t('specialInstruction', 'Special instruction'),
                    rows: 3,
                  },
                ]
              : []),
            {
              name: 'description',
              fieldType: 'String',
              label: 'Description',
              rows: 5,
            },
            ...(isAssetsEnabled && showAssetInCreateWork
              ? [
                  {
                    name: 'assetCodeAndName',
                    fieldType: 'String',
                    label: 'Asset Code and Name',
                    readonly: true,
                  },
                ]
              : []),
            ...(isZonesEnabled
              ? [
                  {
                    name: 'zoneCodeAndName',
                    fieldType: 'String',
                    label: 'Zone Code and Name',
                    readonly: true,
                  },
                ]
              : []),
          ],
          lookups: [],
          validations: [],
          interactions: [],
        }}
      />
      {!contractorView && getOrder && orderId !== undefined && (
        <OrderDetailsDrawer
          open={orderDrawerOpen}
          onClose={() => setOrderDrawerOpen(false)}
          orderId={orderId}
          getOrder={getOrder}
          getMetadata={getOrderMetadata}
          getTenantWorkTypes={services.getTenantWorkTypes}
          workTypeId={
            typeof loadedWork?.workTypeId === 'number'
              ? loadedWork.workTypeId
              : null
          }
        />
      )}
    </TitledCard>
  );
};

export default WorkDetails;
